import styles from './NewAgentTaskForm.module.css'
import {clsx} from 'clsx'
import {
  NewTaskChatInput,
  type NewTaskChatInputProps,
  type CreateTaskReference,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
} from '@github-ui/agent-sessions/components/new-task-chat-input/NewTaskChatInput'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {DropdownRepository} from '@github-ui/agent-sessions/hooks/useCopilotRepoFetching'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CopilotChatProvider} from '@github-ui/copilot-chat/CopilotChatContext'
/* eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports --
   preexisting violation https://github.com/github/react-platform/issues/1135 */
import {relayEnvironmentWithMissingFieldHandlerForNode, RelayEnvironmentProvider} from '@github-ui/relay-environment'
import {SkeletonBox} from '@primer/react'
import {useCurrentRepository, type Repository} from '@github-ui/current-repository'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {uuid} from '@github-ui/agent-sessions/utils/uuid'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useCopilotChatPayloadQuery} from '@github-ui/global-copilot-menu/hooks/useCopilotChatPayloadQuery'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {CreateTaskParams} from '@github-ui/agent-sessions/types/task'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'

const environment = relayEnvironmentWithMissingFieldHandlerForNode()

export type NewAgentTaskFormProps = {
  createTask: (task: CreateTaskParams) => void
  currentRepo?: Repository
  currentReference?: CreateTaskReference
  onCreate?: () => void
  showHeader?: boolean
  hideRepoSelector?: boolean
  eventType: string
  isInPortal?: NewTaskChatInputProps['isInPortal']
  textAreaRef?: NewTaskChatInputProps['textAreaRef']
  className?: string
}

export function NewAgentTaskForm({
  createTask,
  currentRepo,
  currentReference,
  onCreate,
  showHeader = true,
  hideRepoSelector,
  eventType,
  isInPortal,
  textAreaRef,
  className,
}: NewAgentTaskFormProps) {
  const copilotChatPayload = useCopilotChatPayloadQuery()
  const currentRepoFromContext = useCurrentRepository()
  const initialRepo = currentRepo || currentRepoFromContext

  const handleUserSubmit = async (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    reference?: string,
    modelId?: string,
    createPullRequest?: boolean,
  ) => {
    const trimmedContent = content.trim()
    if (trimmedContent === '') return
    if (!selectedRepo) return
    const newTask = {
      id: uuid(),
      nwo: selectedRepo.nameWithOwner,
      problemStatement: trimmedContent,
      baseRef: selectedBranch || selectedRepo.defaultBranch,
      reference,
      eventType,
      eventContent: trimmedContent,
      selectedAgent,
      modelId,
      createPullRequest,
    }
    createTask(newTask)

    onCreate?.()
  }

  return (
    <>
      {showHeader && (
        <div className={styles.header}>
          <h2 className="f5">New agent session</h2>
        </div>
      )}
      <div className={clsx(styles.content, !showHeader && 'pt-3', className)}>
        {copilotChatPayload ? (
          <RelayEnvironmentProvider environment={environment}>
            <CopilotChatProvider
              workerPath={copilotChatPayload.findFileWorkerPath}
              threadId={null}
              refs={copilotChatPayload.refs || []}
              mode="immersive"
              ssoOrganizations={copilotChatPayload.ssoOrganizations || []}
              copilotChatPayload={copilotChatPayload}
            >
              <NewTaskChatInput
                handleUserSubmit={handleUserSubmit}
                initialRepo={initialRepo}
                initialReference={currentReference}
                hideRepoSelector={hideRepoSelector}
                isInPortal={isInPortal}
                textAreaRef={textAreaRef}
                size="small"
                eventType={eventType}
              />
            </CopilotChatProvider>
          </RelayEnvironmentProvider>
        ) : (
          <SkeletonBox className={styles.skeleton} />
        )}
      </div>
    </>
  )
}

try{ NewAgentTaskForm.displayName ||= 'NewAgentTaskForm' } catch {}